import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Typography,
} from '@mui/material';
import { StyledSnackbar } from '../../common/AppSnackbar';
import ApiKeyCreateForm from './ApiKeyCreateForm';
import ApiKeyList from './ApiKeyList';
import NewApiKeyAlert from './NewApiKeyAlert';
import '../OrganizationSettings.css';
import {
  ADMIN_SCOPE,
  COPY_FEEDBACK_DURATION,
  createApiKey,
  deleteApiKey,
  EDITOR_SCOPE,
  fetchApiKeys,
  fetchCurrentUser,
  MAX_API_KEYS,
  retrieveAllowedWorkflowsByApiKeyId,
  SNACKBAR_DURATION,
  styles,
  updateAllowedWorkflowsByApiKeyId,
  USER_SCOPE,
} from './ApiKeyUtil';
import { getWorkflows } from '../../../api/workflows';

const ApiKeyDialog = ({ open, onClose }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState([USER_SCOPE]);
  const [scopeError, setScopeError] = useState(false);
  const [selectedWorkflows, setSelectedWorkflows] = useState([]);
  const [workflowError, setWorkflowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiKeyCreated, setApiKeyCreated] = useState(null);
  const [error, setError] = useState('');
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [copyIcon, setCopyIcon] = useState('copy');
  const [showNewKey, setShowNewKey] = useState(false);
  const [keyPendingDeletion, setKeyPendingDeletion] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [allWorkflows, setAllWorkflows] = useState([]);
  const [isFetchingAllWorkflows, setIsFetchingAllWorkflows] = useState(false);

  useEffect(() => {
    if (open) {
      setInitialLoading(true);
      handleFetchCurrentUser();

      Promise.all([handleFetchApiKeys(), handleFetchWorkflows()])
        .then(populateWorkflowNameInApiKeys)
        .catch(error => {
          console.error('Error loading API keys and workflows:', error);
        });
    }
  }, [open]);

  //Need to populate workflow names because User Access projects table does not contain workflow names
  const populateWorkflowNameInApiKeys = ([apiKeysResponse, workflowsResponse]) => {
    if (apiKeysResponse?.data && workflowsResponse?.data) {
      const apiKeysWithAllowedWorkflowsLoaded = loadAllowedWorkflowsForWorkflowEditorApiKeys(
        apiKeysResponse.data,
        workflowsResponse.data.map(workflow => ({
          projectId: workflow.projectId,
          projectName: workflow.name || workflow.projectId,
        }))
      );
      apiKeysWithAllowedWorkflowsLoaded.then(enriched => setApiKeys(enriched));
    }
  };

  const handleFetchCurrentUser = async () => {
    try {
      const response = await fetchCurrentUser();
      if (response?.data) {
        const { roleAdministrator, roleOwner } = response.data;
        const userIsAdmin = roleAdministrator || roleOwner;
        setCurrentUser(response.data);
        setIsAdmin(userIsAdmin);

        if (!userIsAdmin) {
          setSelectedScopes([USER_SCOPE]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      setIsAdmin(false);
      setSelectedScopes([USER_SCOPE]);
    }
  };

  const handleFetchApiKeys = async () => {
    setIsLoading(true);
    const response = await fetchApiKeys();

    if (response?.data) {
      setApiKeys(response.data);
    } else {
      setError(response?.response?.data?.message || 'Failed to fetch API keys');
    }

    setIsLoading(false);
    setInitialLoading(false);
    return response;
  };

  const createWorkflowMap = workflows => {
    const workflowMap = new Map();
    workflows.forEach(workflow => {
      workflowMap.set(workflow.projectId, workflow.projectName);
    });
    return workflowMap;
  };

  const populatedMissingWorkflowNames = (workflow, workflowMap) => ({
    ...workflow,
    projectName: workflow.projectName || workflowMap.get(workflow.projectId) || workflow.projectId,
  });

  const populateApiKeyWorkflowsWithName = async (apiKey, workflowMap) => {
    if (!apiKey.scopes.includes(EDITOR_SCOPE)) {
      return apiKey;
    }

    try {
      const workflowResponse = await retrieveAllowedWorkflowsByApiKeyId(apiKey.id);
      const allowedWorkflows = workflowResponse?.data || [];
      const workflowsWithWorklowName = allowedWorkflows.map(workflow =>
        populatedMissingWorkflowNames(workflow, workflowMap)
      );

      return {
        ...apiKey,
        workflows: workflowsWithWorklowName,
      };
    } catch (error) {
      console.error(`Failed to fetch workflows for API key ${apiKey.id}:`, error);
      return {
        ...apiKey,
        workflows: [],
      };
    }
  };

  const loadAllowedWorkflowsForWorkflowEditorApiKeys = async (apiKeys, allWorkflows) => {
    const workflowMap = createWorkflowMap(allWorkflows);
    return Promise.all(apiKeys.map(apiKey => populateApiKeyWorkflowsWithName(apiKey, workflowMap)));
  };

  const handleFetchWorkflows = async () => {
    setIsFetchingAllWorkflows(true);
    try {
      const response = await getWorkflows();
      if (response?.data) {
        const workflowData = response.data.map(workflow => ({
          projectId: workflow.projectId,
          projectName: workflow.name || workflow.projectId,
        }));
        setAllWorkflows(workflowData);
      }
      setIsFetchingAllWorkflows(false);
      return response;
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
      setIsFetchingAllWorkflows(false);
      throw error;
    }
  };

  const handleScopeChange = (scope, checked) => {
    setScopeError(false);
    setError('');
    setWorkflowError(false);

    if (checked) {
      let newScopes = [...selectedScopes, scope];

      if (scope === ADMIN_SCOPE && selectedScopes.includes(EDITOR_SCOPE)) {
        newScopes = newScopes.filter(s => s !== EDITOR_SCOPE);
        setSelectedWorkflows([]);
      } else if (scope === EDITOR_SCOPE && selectedScopes.includes(ADMIN_SCOPE)) {
        newScopes = newScopes.filter(s => s !== ADMIN_SCOPE);
      }

      setSelectedScopes(newScopes);
    } else {
      setSelectedScopes(prev => prev.filter(s => s !== scope));

      if (scope === EDITOR_SCOPE) {
        setSelectedWorkflows([]);
      }
    }
  };

  const handleWorkflowChange = workflows => {
    setSelectedWorkflows(workflows);
    setWorkflowError(false);
    setError('');
  };

  const handleCreateApiKey = async () => {
    const trimmedName = newApiKeyName.trim();

    if (!trimmedName) {
      setError('Please enter a name for your API key');
      return;
    }

    if (selectedScopes.length === 0) {
      setScopeError(true);
      setError('Please select at least one scope for your API key');
      return;
    }

    if (selectedScopes.includes(EDITOR_SCOPE) && selectedWorkflows.length === 0) {
      setWorkflowError(true);
      setError('Please select at least one workflow for Editor scope');
      return;
    }

    if (apiKeys.length >= MAX_API_KEYS) {
      setError(`You have reached the maximum limit of ${MAX_API_KEYS} API keys.`);
      return;
    }

    if (selectedScopes.includes(ADMIN_SCOPE) && !isAdmin) {
      setError('You do not have permission to create API keys with Admin scope');
      return;
    }

    setIsLoading(true);
    setError('');
    setScopeError(false);
    setWorkflowError(false);

    const requestData = {
      name: trimmedName,
      scopes: selectedScopes,
      ...(selectedScopes.includes(EDITOR_SCOPE) && {
        allowedWorkflowIds: selectedWorkflows?.map(workflow => workflow.projectId) || [],
      }),
    };

    const response = await createApiKey(requestData);

    if (response?.data) {
      const { apiKey, ...newApiKey } = response.data;
      setApiKeyCreated(response.data);
      setNewApiKeyName('');
      setSelectedScopes(isAdmin ? [ADMIN_SCOPE] : [USER_SCOPE]);
      newApiKey.workflows = selectedWorkflows;
      setSelectedWorkflows([]);
      setApiKeys([...apiKeys, newApiKey]);
    } else {
      setError(response?.response?.data?.message || 'Failed to create API key');
    }

    setIsLoading(false);
  };

  const startDeleteConfirmation = key => {
    setError('');
    setKeyPendingDeletion(key.id);
  };

  const cancelDeletion = () => {
    setKeyPendingDeletion(null);
  };

  const handleDeleteApiKey = async id => {
    setIsLoading(true);

    deleteApiKey(id)
      .then(response => {
        if (response?.data) {
          setApiKeys(prevKeys => prevKeys.filter(apiKey => apiKey.id !== id));
        } else {
          setError(response?.response?.data?.message || 'Failed to delete API key');
        }
      })
      .finally(() => {
        setKeyPendingDeletion(null);
        setIsLoading(false);
      });
  };

  const handleUpdateApiKeyWorkflows = async (apiKeyId, workflows) => {
    try {
      const response = await updateAllowedWorkflowsByApiKeyId(apiKeyId, workflows);

      if (response?.status >= 200 && response?.status < 300) {
        setApiKeys(prevKeys =>
          prevKeys.map(key => (key.id === apiKeyId ? { ...key, workflows: workflows } : key))
        );
      } else {
        throw new Error(
          response?.data?.message ||
            response?.response?.data?.message ||
            'Failed to update workflows'
        );
      }
    } catch (error) {
      throw error;
    }
  };

  const copyToClipboard = text => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopyFeedback(true);
        setCopyIcon('check');
        setTimeout(() => setCopyIcon('copy'), COPY_FEEDBACK_DURATION);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  const closeFeedback = () => {
    setCopyFeedback(false);
  };

  const resetDialog = () => {
    setApiKeyCreated(null);
    setError('');
    setNewApiKeyName('');
    setSelectedScopes(isAdmin ? [ADMIN_SCOPE] : [USER_SCOPE]);
    setScopeError(false);
    setSelectedWorkflows([]);
    setWorkflowError(false);
    setShowNewKey(false);
  };

  const toggleKeyVisibility = () => {
    setShowNewKey(prev => !prev);
  };

  const closeDialog = () => {
    resetDialog();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      fullWidth
      maxWidth="md"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
    >
      <DialogTitle>
        <Box sx={styles.dialog.header}>
          <Typography variant="h6">API Keys</Typography>
          <IconButton onClick={closeDialog} size="small">
            <span style={styles.dialog.closeButton}>&times;</span>
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={styles.dialog.description}>
          API keys allow secure programmatic access to your Workflow86 account. Keep your keys
          secret.
        </Typography>

        <Collapse in={!!apiKeyCreated} timeout={500}>
          {apiKeyCreated && (
            <NewApiKeyAlert
              apiKeyCreated={apiKeyCreated}
              showNewKey={showNewKey}
              onToggleVisibility={toggleKeyVisibility}
              onCopyKey={copyToClipboard}
              copyIcon={copyIcon}
            />
          )}
        </Collapse>

        <Collapse in={!!error} timeout={500}>
          {error && (
            <Alert severity="error" sx={styles.alert.base}>
              {error}
            </Alert>
          )}
        </Collapse>

        <ApiKeyCreateForm
          newApiKeyName={newApiKeyName}
          onNameChange={setNewApiKeyName}
          onCreateKey={handleCreateApiKey}
          isLoading={isLoading}
          apiKeysCount={apiKeys.length}
          selectedScopes={selectedScopes}
          onScopeChange={handleScopeChange}
          isAdmin={isAdmin}
          scopeError={scopeError}
          selectedWorkflows={selectedWorkflows}
          onWorkflowChange={handleWorkflowChange}
          workflowError={workflowError}
          currentUser={currentUser}
          allWorkflows={allWorkflows}
          isFetchingAllWorkflows={isFetchingAllWorkflows}
        />

        <Typography variant="subtitle2" sx={styles.dialog.sectionTitle}>
          Your API Keys
        </Typography>

        <ApiKeyList
          apiKeys={apiKeys}
          initialLoading={initialLoading}
          keyPendingDeletion={keyPendingDeletion}
          onDeleteStart={startDeleteConfirmation}
          onDeleteCancel={cancelDeletion}
          onDeleteConfirm={handleDeleteApiKey}
          onUpdateWorkflows={handleUpdateApiKeyWorkflows}
          allWorkflows={allWorkflows}
          isFetchingAllWorkflows={isFetchingAllWorkflows}
          isLoading={isLoading}
        />
      </DialogContent>

      <StyledSnackbar
        open={copyFeedback}
        message="API key copied to clipboard"
        severity="success"
        onClose={closeFeedback}
        autoHideDuration={SNACKBAR_DURATION}
      />
    </Dialog>
  );
};

export default ApiKeyDialog;
